import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useApi } from "../../api/ApiProvider";
import { useIsChief } from "../../hooks/useChiefDebugAccess";

interface AboutDialogProps {
  open: boolean;
  onClose: () => void;
}

interface ApiInfo {
  version?: string;
  buildDate?: string;
  schema?: string;
  host?: string;
  uptime?: number;
  memory?: {
    used: string;
    total: string;
  };
}

const formatBuildDate = (value?: string): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return value; // e.g. "unknown" / "local"
  return date.toLocaleString("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const formatUptime = (seconds?: number): string => {
  if (seconds == null) return "—";
  const total = Math.floor(seconds);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  parts.push(`${secs}s`);
  return parts.join(" ");
};

const InfoRow = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      gap: 2,
      py: 0.75,
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      // Hostnames are long and have no spaces to wrap on, so break anywhere
      // rather than letting them push the label out of the dialog.
      sx={{ fontWeight: 500, textAlign: "right", minWidth: 0, overflowWrap: "anywhere" }}
    >
      {value ?? "—"}
    </Typography>
  </Box>
);

export const AboutDialog: React.FC<AboutDialogProps> = observer(({ open, onClose }) => {
  const { user } = useApi();
  const isChief = useIsChief();
  const [info, setInfo] = useState<ApiInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const frontendVersion = process.env.REACT_APP_INSTANCE_VERSION;
  const frontendBuildDate = process.env.REACT_APP_INSTANCE_BUILD_DATE;
  const instanceName = process.env.REACT_APP_INSTANCE_NAME;

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const fetchInfo = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = (await user.apiInfo()) as ApiInfo;
        if (!cancelled) setInfo(data);
      } catch (e) {
        if (!cancelled) setError("Serverinformationen konnten nicht geladen werden.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchInfo();
    return () => {
      cancelled = true;
    };
  }, [open, user]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Über Mobili Decision</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">Mobili Decision</Typography>
          <Typography variant="body2" color="text.secondary">
            {instanceName ? `Instanz: ${instanceName}` : "Instanz: —"}
          </Typography>
        </Box>

        <Divider textAlign="left" sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Anwendung (Frontend)
          </Typography>
        </Divider>
        <InfoRow label="Version" value={frontendVersion || "unbekannt"} />
        <InfoRow label="Build-Datum" value={formatBuildDate(frontendBuildDate)} />

        <Divider textAlign="left" sx={{ my: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Server (API)
          </Typography>
        </Divider>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && !loading && (
          <Alert severity="warning" sx={{ my: 1 }}>
            {error}
          </Alert>
        )}

        {info && !loading && !error && (
          <>
            <InfoRow label="Version" value={info.version || "unbekannt"} />
            <InfoRow label="Build-Datum" value={formatBuildDate(info.buildDate)} />
            {info.schema && <InfoRow label="DB" value={info.schema} />}
            {/* Which server the API talks to is chief-only. */}
            {isChief && info.host && <InfoRow label="DB-Host" value={info.host} />}
            <InfoRow label="Laufzeit" value={formatUptime(info.uptime)} />
            {info.memory && (
              <InfoRow
                label="Speicher"
                value={`${info.memory.used} / ${info.memory.total}`}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" autoFocus>
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default AboutDialog;
